import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Truck, RefreshCw, Shield, Star } from 'lucide-react';
import Header from './components/Header1';
import Footer from './components/Footer1';
import Button from './components/Button';
import ProductCard from './components/ProductCard';

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedSize, setSelectedSize] = useState('M');
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);

    const product = {
        id: id,
        name: 'Chanderi Silk Saree',
        price: 8999,
        originalPrice: 11999,
        rating: 4.8,
        reviews: 96,
        description: 'A stunning Chanderi silk saree featuring delicate hand-woven motifs and a rich pallu. Perfect for festive occasions and celebrations, this saree embodies the timeless elegance of Indian craftsmanship.',
        features: [
            'Pure Chanderi silk fabric',
            'Hand-woven traditional motifs',
            'Includes matching blouse piece',
            'Zari border detailing',
            'Lightweight and comfortable drape',
        ],
        images: [
            'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=800',
        ],
        sizes: ['S', 'M', 'L', 'XL'],
        inStock: true,
    };

    const relatedProducts = [
        { id: 2, name: 'Banarasi Brocade Lehenga', category: 'Lehengas', price: 24999, image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Organza Printed Saree', category: 'Sarees', price: 6799, image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Velvet Bridal Lehenga', category: 'Lehengas', price: 32999, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Cotton Chikankari Kurta', category: 'Kurtas', price: 2999, image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    return (
        <div className="min-h-screen bg-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
            <Header cartCount={3} />

            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10 lg:py-12">
                {/* Product Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 mb-12 md:mb-20">
                    {/* Images */}
                    <div>
                        <div className="bg-[#F8FAF8] mb-3 md:mb-4 overflow-hidden">
                            <img
                                src={product.images[selectedImage]}
                                alt={product.name}
                                className="w-full h-auto"
                            />
                        </div>
                        <div className="grid grid-cols-4 gap-2 md:gap-3">
                            {product.images.map((image, index) => (
                                <button
                                    key={index}
                                    onClick={() => setSelectedImage(index)}
                                    className={`bg-[#F8FAF8] overflow-hidden border-2 transition-colors ${
                                        selectedImage === index ? 'border-[#2D6A4F]' : 'border-transparent'
                                    }`}
                                >
                                    <img src={image} alt={`View ${index + 1}`} className="w-full h-auto" />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Product Info */}
                    <div>
                        <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-[#1A1A1A] mb-3">{product.name}</h1>

                        {/* Rating */}
                        <div className="flex items-center gap-3 mb-3 md:mb-4">
                            <div className="flex gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} size={14} className={i < Math.floor(product.rating) ? 'fill-[#2D6A4F] text-[#2D6A4F]' : 'text-[#D1D5DB]'} />
                                ))}
                            </div>
                            <span className="text-xs md:text-sm text-[#6B7280]">{product.rating} ({product.reviews} reviews)</span>
                        </div>

                        {/* Price */}
                        <div className="flex items-center gap-3 mb-4 md:mb-6">
                            <span className="text-2xl md:text-3xl font-semibold text-[#1A1A1A]">{'\u20B9'}{product.price.toLocaleString('en-IN')}</span>
                            {product.originalPrice && (
                                <>
                                    <span className="text-base md:text-lg text-[#9CA3AF] line-through">{'\u20B9'}{product.originalPrice.toLocaleString('en-IN')}</span>
                                    <span className="bg-[#2D6A4F] text-white px-2 py-0.5 text-xs font-medium">
                                        {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
                                    </span>
                                </>
                            )}
                        </div>

                        {/* Description */}
                        <p className="text-sm md:text-base text-[#6B7280] mb-5 md:mb-6 leading-relaxed">{product.description}</p>

                        {/* Size Selector */}
                        <div className="mb-5 md:mb-6">
                            <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Select Size</label>
                            <div className="flex gap-2 md:gap-3">
                                {product.sizes.map(size => (
                                    <button
                                        key={size}
                                        onClick={() => setSelectedSize(size)}
                                        className={`px-4 md:px-5 py-2 md:py-2.5 text-sm font-medium border transition-colors ${
                                            selectedSize === size
                                                ? 'border-[#2D6A4F] bg-[#2D6A4F] text-white'
                                                : 'border-[#E5E7EB] text-[#1A1A1A] hover:border-[#2D6A4F]'
                                        }`}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Quantity */}
                        <div className="mb-6 md:mb-8">
                            <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Quantity</label>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    className="w-10 h-10 border border-[#E5E7EB] flex items-center justify-center hover:border-[#2D6A4F] transition-colors text-[#1A1A1A] font-medium"
                                >
                                    -
                                </button>
                                <span className="text-base font-medium w-10 text-center text-[#1A1A1A]">{quantity}</span>
                                <button
                                    onClick={() => setQuantity(quantity + 1)}
                                    className="w-10 h-10 border border-[#E5E7EB] flex items-center justify-center hover:border-[#2D6A4F] transition-colors text-[#1A1A1A] font-medium"
                                >
                                    +
                                </button>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 mb-6 md:mb-8">
                            <Button variant="primary" size="lg" fullWidth onClick={() => navigate('/template1/cart')}>
                                <ShoppingCart size={18} className="mr-2" />
                                Add to Cart
                            </Button>
                            <button className="w-12 h-12 flex-shrink-0 border border-[#E5E7EB] flex items-center justify-center hover:border-[#2D6A4F] hover:text-[#2D6A4F] transition-colors">
                                <Heart size={18} />
                            </button>
                        </div>

                        {/* Features */}
                        <div className="border-t border-[#E5E7EB] pt-5 md:pt-6">
                            <h3 className="text-sm font-semibold text-[#1A1A1A] mb-3">Product Details</h3>
                            <ul className="space-y-2">
                                {product.features.map((feature, index) => (
                                    <li key={index} className="flex items-start gap-2 text-sm text-[#6B7280]">
                                        <span className="text-[#2D6A4F] mt-1">&#8226;</span>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Info Cards */}
                        <div className="grid grid-cols-3 gap-3 mt-6 md:mt-8">
                            <div className="text-center p-3 md:p-4 bg-[#F8FAF8]">
                                <Truck size={20} className="mx-auto mb-1.5 text-[#2D6A4F]" />
                                <p className="text-[10px] md:text-xs text-[#6B7280]">Free Shipping</p>
                            </div>
                            <div className="text-center p-3 md:p-4 bg-[#F8FAF8]">
                                <RefreshCw size={20} className="mx-auto mb-1.5 text-[#2D6A4F]" />
                                <p className="text-[10px] md:text-xs text-[#6B7280]">Easy Returns</p>
                            </div>
                            <div className="text-center p-3 md:p-4 bg-[#F8FAF8]">
                                <Shield size={20} className="mx-auto mb-1.5 text-[#2D6A4F]" />
                                <p className="text-[10px] md:text-xs text-[#6B7280]">Secure Payment</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Related Products */}
                <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-6 md:mb-8">You May Also Like</h2>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5 lg:gap-6">
                        {relatedProducts.map(product => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ProductDetail;
